import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { TIERS } from "@/lib/tiers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Edit, Trash2, ShieldOff, ShieldCheck, Skull } from "lucide-react";
import Avatar from "@/components/Avatar";
import RoleBadge from "@/components/RoleBadge";

// Local status pill — bigger dot, clearer pill, consistent height so the
// column visually aligns row-to-row.
function StatusPill({ suspended }) {
  if (suspended) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-rose-950/50 text-rose-300 border border-rose-500/40"
        data-testid="user-status-suspended"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shadow-[0_0_6px_rgba(244,63,94,0.8)]" />
        Suspended
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-emerald-950/40 text-emerald-300 border border-emerald-500/40"
      data-testid="user-status-active"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
      Active
    </span>
  );
}

// Uniformly-sized icon-only action button. Title attr drives the tooltip so
// every button feels the same height/width and the row gutter stays tight.
function ActionBtn({ onClick, title, className = "", testid, children }) {
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={onClick}
      title={title}
      aria-label={title}
      data-testid={testid}
      className={`h-8 w-8 p-0 inline-flex items-center justify-center border-white/10 text-zinc-300 hover:text-white hover:bg-white/5 ${className}`}
    >
      {children}
    </Button>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/users", { params: { q } }); setUsers(data.users || []); }
    catch (e) { console.error("Failed to load users:", e); toast.error("Failed to load users"); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const update = async (id, body) => {
    try {
      await api.put(`/admin/users/${id}`, body);
      load();
      toast.success("Updated");
      setEditing(null);
    } catch (e) { console.error("Update user failed:", e); toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/admin/users/${id}`); load(); toast.success("Deleted"); }
    catch (e) { console.error("Delete user failed:", e); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleCultMaster = async (u) => {
    const has = (u.badges || []).includes("cult_master");
    try {
      if (has) {
        await api.delete(`/admin/users/${u.id}/cult-master`);
        toast.success(`Cult Master revoked from ${u.name}`);
      } else {
        await api.post(`/admin/users/${u.id}/cult-master`);
        toast.success(`Cult Master granted to ${u.name}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-white">User Management</h2>
          <p className="text-xs text-zinc-500 mt-0.5">{users.length} user{users.length === 1 ? "" : "s"}</p>
        </div>
        <Input placeholder="Search users…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64 bg-black/40 border-white/10" data-testid="admin-users-search" />
      </div>
      <div className="glass rounded-xl overflow-hidden border border-white/10">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col className="w-[22%]" />
              <col className="w-[26%]" />
              <col className="w-[14%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[14%]" />
            </colgroup>
            <thead className="text-zinc-400 text-[11px] uppercase tracking-wider bg-black/50">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Role</th>
                <th className="text-left px-4 py-3 font-semibold">Expires</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, idx) => {
                const isCultMaster = (u.badges || []).includes("cult_master");
                return (
                  <tr
                    key={u.id}
                    className={`border-t border-white/5 hover:bg-white/[0.03] transition-colors ${idx % 2 === 1 ? "bg-white/[0.015]" : ""}`}
                    data-testid={`admin-user-row-${u.id}`}
                  >
                    <td className="px-4 py-3 text-white">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Avatar user={u} size={28} />
                        <span className="truncate font-medium" title={u.name}>{u.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-300">
                      <span className="block truncate" title={u.email}>{u.email}</span>
                    </td>
                    <td className="px-4 py-3">
                      <RoleBadge role={u.role} badges={u.badges} size="xs" />
                    </td>
                    <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">
                      {u.membership_end ? new Date(u.membership_end).toLocaleDateString() : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill suspended={u.suspended} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5 flex-nowrap">
                        <ActionBtn
                          onClick={() => setEditing(u)}
                          title="Edit user"
                          testid={`edit-user-${u.id}`}
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </ActionBtn>
                        <ActionBtn
                          onClick={() => toggleCultMaster(u)}
                          title={isCultMaster ? "Revoke Cult Master" : "Grant Cult Master"}
                          className={isCultMaster ? "border-rose-500/60 text-rose-300 bg-rose-950/30 hover:bg-rose-950/50" : ""}
                          testid={`cult-master-toggle-${u.id}`}
                        >
                          <Skull className="w-3.5 h-3.5" />
                        </ActionBtn>
                        <ActionBtn
                          onClick={() => update(u.id, { suspended: !u.suspended })}
                          title={u.suspended ? "Unsuspend user" : "Suspend user"}
                          className={u.suspended ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-950/30" : "border-amber-500/40 text-amber-300 hover:bg-amber-950/30"}
                          testid={`suspend-user-${u.id}`}
                        >
                          {u.suspended ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
                        </ActionBtn>
                        <ActionBtn
                          onClick={() => remove(u.id)}
                          title="Delete user"
                          className="border-rose-500/40 text-rose-300 hover:bg-rose-950/40 hover:text-rose-200"
                          testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </ActionBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-zinc-500 italic">No users.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white">
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {editing && <EditUserForm user={editing} onSave={update} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditUserForm({ user, onSave }) {
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState(user.role);
  const [extendDays, setExtendDays] = useState(0);
  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs text-zinc-400">Name</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-black/40 border-white/10" data-testid="edit-user-name" />
      </div>
      <div>
        <label className="text-xs text-zinc-400">Role</label>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="mt-1 bg-black/40 border-white/10" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-white/10 text-white">
            {["member", "elite_cultist", "hardcore_cultist", "god_tier_supporter", "admin"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs text-zinc-400">Extend membership by days</label>
        <Input type="number" min={0} value={extendDays} onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)} className="mt-1 bg-black/40 border-white/10" data-testid="edit-user-extend" />
      </div>
      <Button onClick={() => onSave(user.id, { name, role, extend_days: extendDays || undefined })} className="bg-rose-600 hover:bg-rose-500 w-full" data-testid="edit-user-save">Save Changes</Button>
    </div>
  );
}
